import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Logo } from './Logo';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import { useSendResetCode, useVerifyResetCode, useResetPassword } from '../services/passwordResetService';

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBackToLogin: () => void;
}

type Step = 'email' | 'verify' | 'newPassword' | 'success';

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ isOpen, onClose, onBackToLogin }) => {
  const { t } = useTranslation();
  
  useBodyScrollLock(isOpen);

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetId, setResetId] = useState<number | null>(null);
  const [, setLoginId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const sendResetCodeMutation = useSendResetCode();
  const verifyResetCodeMutation = useVerifyResetCode();
  const resetPasswordMutation = useResetPassword();

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setStep('email');
      setEmail('');
      setCode('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      setResetId(null);
      setLoginId(null);
    }
  }, [isOpen]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError(t('auth.forgotPassword.validation.emailRequired'));
      return;
    }

    if (!validateEmail(email)) {
      setError(t('auth.forgotPassword.validation.emailInvalid'));
      return;
    }

    try {
      await sendResetCodeMutation.mutateAsync(email);
      setStep('verify');
    } catch (err: any) {
      setError(err.message || t('auth.forgotPassword.errors.sendFailed'));
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!code.trim()) {
      setError(t('auth.forgotPassword.validation.codeRequired'));
      return;
    }

    if (code.length !== 6 || !/^\d+$/.test(code)) {
      setError(t('auth.forgotPassword.validation.codeInvalid'));
      return;
    }

    try {
      const result = await verifyResetCodeMutation.mutateAsync({ email, code });
      if (result.data?.resetId) {
        setResetId(result.data.resetId);
      }
      if (result.data?.loginId) {
        setLoginId(result.data.loginId);
        console.log('Found loginId for password reset:', result.data.loginId);
      } else {
        console.warn('No loginId found for email:', email);
      }
      setStep('newPassword');
    } catch (err: any) {
      setError(err.message || t('auth.forgotPassword.errors.verifyFailed'));
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!newPassword) {
      setError(t('auth.forgotPassword.validation.newPasswordRequired'));
      return;
    }

    if (newPassword.length < 6) {
      setError(t('auth.forgotPassword.validation.passwordMinLength'));
      return;
    }

    if (!/^(?=.*[A-Za-z])(?=.*\d)/.test(newPassword)) {
      setError(t('auth.forgotPassword.validation.passwordFormat'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('auth.forgotPassword.validation.passwordsNotMatch'));
      return;
    }

    setIsChangingPassword(true);

    try {
      // Use the password reset API endpoint
      await resetPasswordMutation.mutateAsync({
        resetId: resetId?.toString() || '',
        email: email,
        newPassword: newPassword
      });
      
      setStep('success');
    } catch (err: any) {
      setError(err.message || t('auth.forgotPassword.errors.resetFailed'));
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    try {
      await sendResetCodeMutation.mutateAsync(email);
      setCode('');
    } catch (err: any) {
      setError(err.message || t('auth.forgotPassword.errors.sendFailed'));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto" role="dialog" aria-modal="true">
      <div className="bg-dark-800 rounded-lg w-full max-w-md relative my-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-dark-700">
          <div className="text-center flex-1">
            <Logo />
            <h2 className="text-white text-xl mt-1">{t('auth.forgotPassword.title')}</h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close dialog"
            className="text-white hover:text-gray-300 transition-colors duration-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'email' && (
            <form onSubmit={handleSendCode} className="space-y-6">
              <p className="text-gray-300 text-sm mb-4">
                {t('auth.forgotPassword.emailDescription')}
              </p>
              
              <div>
                <label htmlFor="reset-email" className="block text-white text-sm font-medium mb-2">
                  {t('auth.forgotPassword.email')}
                </label>
                <input
                  id="reset-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.forgotPassword.placeholders.email')}
                  className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-green-400 transition-colors duration-200"
                />
              </div>

              {error && (
                <div className="bg-red-900/30 border border-red-500 text-red-200 text-sm rounded-lg px-4 py-2">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={sendResetCodeMutation.isPending}
                className={`w-full text-white font-medium py-3 rounded-lg transition-colors duration-200 ${
                  sendResetCodeMutation.isPending
                    ? 'bg-gray-500 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {sendResetCodeMutation.isPending ? t('common.loading') : t('auth.forgotPassword.sendCode')}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={onBackToLogin}
                  className="text-yellow-400 hover:text-yellow-300 text-sm font-medium transition-colors duration-200"
                >
                  {t('auth.forgotPassword.backToLogin')}
                </button>
              </div>
            </form>
          )}

          {step === 'verify' && (
            <form onSubmit={handleVerifyCode} className="space-y-6">
              <p className="text-gray-300 text-sm mb-4">
                {t('auth.forgotPassword.verifyDescription', { email })}
              </p>
              
              <div>
                <label htmlFor="reset-code" className="block text-white text-sm font-medium mb-2">
                  {t('auth.forgotPassword.verificationCode')}
                </label>
                <input
                  id="reset-code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder={t('auth.forgotPassword.placeholders.code')}
                  maxLength={6}
                  className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-green-400 transition-colors duration-200 text-center text-2xl tracking-widest"
                />
              </div>

              {error && (
                <div className="bg-red-900/30 border border-red-500 text-red-200 text-sm rounded-lg px-4 py-2">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={verifyResetCodeMutation.isPending}
                className={`w-full text-white font-medium py-3 rounded-lg transition-colors duration-200 ${
                  verifyResetCodeMutation.isPending
                    ? 'bg-gray-500 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {verifyResetCodeMutation.isPending ? t('common.loading') : t('auth.forgotPassword.verifyCode')}
              </button>

              <div className="text-center space-y-2">
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={sendResetCodeMutation.isPending}
                  className="text-gray-400 hover:text-white text-sm transition-colors duration-200"
                >
                  {sendResetCodeMutation.isPending ? t('common.loading') : t('auth.forgotPassword.resendCode')}
                </button>
                <br />
                <button
                  type="button"
                  onClick={() => setStep('email')}
                  className="text-yellow-400 hover:text-yellow-300 text-sm font-medium transition-colors duration-200"
                >
                  {t('auth.forgotPassword.changeEmail')}
                </button>
              </div>
            </form>
          )}

          {step === 'newPassword' && (
            <form onSubmit={handleChangePassword} className="space-y-6">
              <p className="text-gray-300 text-sm mb-4">
                {t('auth.forgotPassword.newPasswordDescription')}
              </p>
              
              <div>
                <label htmlFor="new-password" className="block text-white text-sm font-medium mb-2">
                  {t('auth.forgotPassword.newPassword')}
                </label>
                <div className="relative">
                  <input
                    id="new-password"
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={t('auth.forgotPassword.placeholders.newPassword')}
                    className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-3 pr-10 text-white placeholder-gray-400 focus:outline-none focus:border-green-400 transition-colors duration-200"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-300 hover:text-white transition-colors duration-200"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirm-password" className="block text-white text-sm font-medium mb-2">
                  {t('auth.forgotPassword.confirmPassword')}
                </label>
                <div className="relative">
                  <input
                    id="confirm-password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('auth.forgotPassword.placeholders.confirmPassword')}
                    className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-3 pr-10 text-white placeholder-gray-400 focus:outline-none focus:border-green-400 transition-colors duration-200"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-300 hover:text-white transition-colors duration-200"
                  >
                    {showConfirmPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <div className="bg-red-900/30 border border-red-500 text-red-200 text-sm rounded-lg px-4 py-2">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isChangingPassword}
                className={`w-full text-white font-medium py-3 rounded-lg transition-colors duration-200 ${
                  isChangingPassword
                    ? 'bg-gray-500 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {isChangingPassword ? t('common.loading') : t('auth.forgotPassword.resetPassword')}
              </button>
            </form>
          )}

          {step === 'success' && (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 mx-auto bg-green-500/20 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <div>
                <h3 className="text-white text-lg font-medium mb-2">
                  {t('auth.forgotPassword.successTitle')}
                </h3>
                <p className="text-gray-300 text-sm">
                  {t('auth.forgotPassword.successDescription')}
                </p>
              </div>

              <button
                onClick={onBackToLogin}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 rounded-lg transition-colors duration-200"
              >
                {t('auth.forgotPassword.backToLogin')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordModal;

